// User-defined tags (issue #99 / T29). Tags are free strings stored on the row —
// there is no tag registry. The palette a list offers is *derived* from the rows
// themselves (`collectTags`), so a tag exists exactly as long as something carries it.
//
// Invariant everywhere: `tags` is either absent or a non-empty, normalized,
// canonically-ordered array. Never `[]` on the wire.
/** Longest a tag may be; the composer caps its input at the same value. */
export const MAX_TAG_LENGTH = 24;
/** Most tags one row may carry. */
export const MAX_TAGS_PER_ROW = 8;
/** Casefolded comparison/dedupe key. Never displayed. */
export const tagKey = (tag) => tag.toLocaleLowerCase();
// Anything a user could paste that shouldn't survive into a chip. Control chars
// (newlines, tabs) become spaces so a pasted "carry\non" reads "carry on"; invisible
// format chars (zero-width joiners and friends) are dropped outright.
const CONTROL_CHARS = /\p{Cc}/gu;
const FORMAT_CHARS = /\p{Cf}/gu;
const WHITESPACE_RUN = /\s+/g;
/**
 * Trim, collapse internal whitespace, strip control characters and a leading `#`,
 * then cap the length. Returns `null` when nothing usable is left.
 */
export const normalizeTag = (raw) => {
    const cleaned = raw
        .replace(CONTROL_CHARS, " ")
        .replace(FORMAT_CHARS, "")
        .replace(WHITESPACE_RUN, " ")
        .trim()
        .replace(/^#+/, "")
        .trim();
    if (cleaned === "")
        return null;
    return cleaned.slice(0, MAX_TAG_LENGTH).trim();
};
/**
 * Normalize each tag, drop the empties, dedupe by `tagKey` (the lexicographically
 * smallest display string wins a casing collision, so the result is independent of
 * input order), sort by `tagKey`, and cap at `MAX_TAGS_PER_ROW`. Idempotent.
 * `[]` means "no tags" — callers store that as an absent field.
 */
export const normalizeTags = (raw) => {
    const byKey = new Map();
    for (const candidate of raw) {
        const tag = normalizeTag(candidate);
        if (tag === null)
            continue;
        const key = tagKey(tag);
        const existing = byKey.get(key);
        if (existing === undefined || tag < existing)
            byKey.set(key, tag);
    }
    return [...byKey.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, tag]) => tag)
        .slice(0, MAX_TAGS_PER_ROW);
};
/**
 * Auto-tags (issue #180): the names of the templates this row came from, recorded on
 * `provenance.templateSources` at import time. They are *derived*, never stored as
 * row tags, which is what keeps them out of `ItemSnapshot` — so an imported row is not
 * instantly "modified", sync-back never pushes a template's name into itself, and
 * detaching a template drops its tag for free (`detach` filters `templateSources`).
 * A source with no `templateName` is an import the user opted out of tagging.
 */
export const autoTagsOf = (row) => normalizeTags((row.provenance?.templateSources ?? []).flatMap((s) => s.templateName ?? []));
/**
 * Everything a row is tagged with, for display, search and filtering: its own tags
 * first, then its auto-tags. Editing only ever touches `row.tags`.
 */
export const allTags = (row) => {
    const auto = autoTagsOf(row);
    if (auto.length === 0)
        return row.tags ?? [];
    const byKey = new Map((row.tags ?? []).map((t) => [tagKey(t), t]));
    for (const t of auto)
        if (!byKey.has(tagKey(t)))
            byKey.set(tagKey(t), t);
    return [...byKey.values()];
};
/**
 * The palette a composer offers for one-click selection when creating/editing a row —
 * own tags only, never auto-tags. Auto-tags are derived provenance (issue #180), not
 * something a row can genuinely "have" added or removed by hand; offering them for
 * selection would let a user pick a tag that then can't be un-picked the normal way.
 * `collectTags` (own + auto) remains what the toolbar filters by and what a row
 * displays — this is narrower, for the composer's palette only.
 */
export const collectOwnTags = (rows) => {
    const byKey = new Map();
    for (const row of rows) {
        for (const tag of row.tags ?? []) {
            const key = tagKey(tag);
            const existing = byKey.get(key);
            if (existing === undefined || tag < existing)
                byKey.set(key, tag);
        }
    }
    return [...byKey.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([, tag]) => tag);
};
/** OR semantics, case-insensitive, across a row's own and auto tags. An empty filter matches everything. */
export const hasAnyTag = (row, filter) => {
    if (filter.length === 0)
        return true;
    const rowTags = allTags(row);
    if (rowTags.length === 0)
        return false;
    const wanted = new Set(filter.map(tagKey));
    return rowTags.some((tag) => wanted.has(tagKey(tag)));
};
/**
 * The derived palette: every tag used across a list's rows. Deterministic and
 * order-independent — pass items, todos and notes together for one shared palette.
 * Not capped by `MAX_TAGS_PER_ROW`; that limit is per row, not per list.
 */
export const collectTags = (rows) => {
    const byKey = new Map();
    for (const row of rows) {
        for (const tag of allTags(row)) {
            const key = tagKey(tag);
            const existing = byKey.get(key);
            if (existing === undefined || tag < existing)
                byKey.set(key, tag);
        }
    }
    return [...byKey.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, tag]) => tag);
};
//# sourceMappingURL=tags.js.map